import { Box, Chip, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Section } from '../../../../../components/ui/Section';
import { ComplaintsTrend, SupplierEsgProfile } from '../../../../../mocks/data/supplierEsgDashboardData';

export interface OperationalPerformanceTabProps {
  profiles: SupplierEsgProfile[];
}

const TREND_COLOR: Record<ComplaintsTrend, 'success' | 'default' | 'error'> = {
  Improving: 'success',
  Stable: 'default',
  Worsening: 'error',
};

const OperationalPerformanceTab: React.FC<OperationalPerformanceTabProps> = ({ profiles }) => {
  const { t } = useTranslation('esgDashboard');
  return (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('operational.onTimeDeliveryChartTitle', 'On-time delivery by supplier (%)')}</Typography>
            <ResponsiveContainer width="100%"
              height={220}>
              <BarChart data={profiles.map((p) => ({ name: p.name, onTime: p.onTimeDeliveryPercentage }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name"
                  tick={{ fontSize: 10 }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={60} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="onTime"
                  fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Section>
      </Grid>
      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('operational.profileTableTitle', 'Operational performance by supplier')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.supplier', 'Supplier')}</TableCell>
                  <TableCell align="right">{t('operational.onTimeDelivery', 'On-time delivery')}</TableCell>
                  <TableCell align="right">{t('operational.fillRate', 'Fill rate')}</TableCell>
                  <TableCell align="right">{t('operational.qualityScore', 'Quality score')}</TableCell>
                  <TableCell align="center">{t('operational.complaintsTrend', 'Complaints trend')}</TableCell>
                  <TableCell align="right">{t('operational.openComplaints', 'Open complaints')}</TableCell>
                  <TableCell align="right">{t('operational.returns', 'Returns')}</TableCell>
                  <TableCell align="right">{t('operational.overallScore', 'Overall score')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((p) => (
                  <TableRow key={p.supplierId}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell align="right">{p.onTimeDeliveryPercentage}%</TableCell>
                    <TableCell align="right">{p.fillRatePercentage}%</TableCell>
                    <TableCell align="right">{p.qualityScore.toFixed(1)}</TableCell>
                    <TableCell align="center">
                      <Chip label={p.complaintsTrend}
                        size="small"
                        color={TREND_COLOR[p.complaintsTrend]}
                        variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{p.openComplaints}</TableCell>
                    <TableCell align="right">{p.returnsPercentage}%</TableCell>
                    <TableCell align="right">{p.overallPerformanceScore}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>
    </Grid>
  );
};

export default OperationalPerformanceTab;
